import { useSelector } from '@xstate/react';
import cn from 'classnames';
import { useCallback, useContext, useMemo, useState } from 'react';

import { CustomStylesCtx, useTextStyle } from '@webapp/common/components/custom-styles';
import { CssPageBlock, CssQuestionType, CssUiComponent } from '@webapp/common/components/survey-custom';
import { Textfield } from '@webapp/common/components/textfield';
import { setHtml } from '@webapp/common/lib/ui';
import { striptags } from '@webapp/common/lib/utils';
import { AnswerType } from '@webapp/common/resources/survey';

import type { SurveyQuestion } from '@webapp/survey/src/components/questions/lib';
import type { QuestionAnswerActor } from '@webapp/survey/src/entities/answer-machine';
import type { GroupMachineActor } from '@webapp/survey/src/machine/group-machine';
import { useStateSelector } from '@webapp/survey/src/machine/use-state-selector';

import css from './name.css';

const Cell: FC<{ groupActor: GroupMachineActor; answerId: number }> = ({ groupActor, answerId }) => {
    const group = useSelector(groupActor, (g) => g.context);
    const responseActor = group.responses[answerId];
    const response = useSelector(responseActor, (r) => r.context);
    const handleChangeValue = useCallback(
        (value: string) => groupActor.send({ type: 'CHANGE_RESPONSE_VALUE', answerId, value }),
        [groupActor, answerId],
    );
    return (
        <div className={cn(CssUiComponent.CELL, css.cell, css.answer)} key={group.id}>
            <div className={css.field}>
                <div className={css.text}>
                    <Textfield
                        transparent
                        placeholder={group.name ? striptags(group.name) : undefined}
                        value={response.value?.toString() || ''}
                        onChange={handleChangeValue}
                    />
                </div>
            </div>
        </div>
    );
};
export const Name: VFC<SurveyQuestion> = ({ question: { answersActors, groupsActors }, questionBlockPosition }) => {
    const { text: textStyle } = useContext(CustomStylesCtx);
    const { itemsGap } = useTextStyle();
    const [, updateRerenderHack] = useState(1);

    const withTitle = useMemo(
        () => answersActors.some((answer) => !!answer.getSnapshot().context.value[0]),
        [answersActors],
    );

    const gridStyle: CSSProperties = useMemo(() => {
        const colCount = groupsActors.length + Number(withTitle);
        return {
            gridTemplateColumns: `repeat(${colCount}, minmax(min-content, max-content))`,
            rowGap: itemsGap,
            justifyContent: questionBlockPosition,
        };
    }, [groupsActors.length, itemsGap, withTitle]);

    const fieldsStyle = useMemo(
        () => ({
            rowGap: itemsGap,
        }),
        [itemsGap],
    );
    const questions = useStateSelector((s) => s.questions);
    return (
        <div className={cn(CssQuestionType.QUESTION, CssQuestionType.NAME, css.name)} style={gridStyle}>
            {answersActors.map((answerActor) => {
                const answer = answerActor.getSnapshot().context;
                let forwardParentAnswer: QuestionAnswerActor | null = null;
                let label = answer.value[0];
                questions.forEach((q) => {
                    const founded = q.getSnapshot().context.answersActors.find((a) => {
                        return a.getSnapshot().context.id === answer.forwardParent;
                    });
                    if (founded) {
                        forwardParentAnswer = founded;
                    }
                });
                if (forwardParentAnswer && forwardParentAnswer.getSnapshot().context.type === AnswerType.SELF) {
                    const sub = () => updateRerenderHack((prev) => (prev + 1) % 10);
                    label = forwardParentAnswer.getSnapshot().context.response.getSnapshot().context.extra?.toString();
                    forwardParentAnswer.subscribe(sub);
                }
                if (answer.hidden || answer.hiddenByAnswersHide) return null;
                return (
                    <div
                        className={cn(CssPageBlock.ANSWER, css.row, !withTitle && css.noLabel)}
                        key={answer.id}
                        style={fieldsStyle}
                    >
                        {withTitle && (
                            <div
                                className={cn(CssUiComponent.LABEL, css.cell, css.label)}
                                style={textStyle}
                                {...setHtml(label || '')}
                            />
                        )}
                        <div className={css.fields} style={fieldsStyle}>
                            {groupsActors.map((groupActor) => {
                                const group = groupActor.getSnapshot().context;
                                return <Cell key={group.id} answerId={answer.id} groupActor={groupActor} />;
                            })}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};
Name.displayName = 'Name';
